/**
 * ================================================================================================
 *  THREE ACTIONS BESIDE ONE ISSUED DOCUMENT — צפייה · הורדה · שליחה חוזרת במייל
 * ================================================================================================
 *
 * The Owner, 2026-11-04:
 *
 *     *"בכרטיס הלקוח במנהל הפלטפורמה, תחת „חיובים וחשבוניות", אני רוצה לראות את החשבוניות שהופקו
 *      לו. ליד כל חשבונית יהיו שלוש פעולות למנהל בעל הרשאה מתאימה: „צפייה", „הורדה" ו„שליחה
 *      חוזרת במייל". השליחה החוזרת תפתח חלון עם מספר החשבונית וכתובת הדואר לחיוב כברירת מחדל,
 *      תאפשר כתובת אחרת חד-פעמית, תאשר את מספר החשבונית ואת היעד לפני השליחה … תעד מי שלח, מתי,
 *      לאיזו כתובת ומה תוצאת השליחה בכרטיס הלקוח, והצג במפורש אם החשבונית טרם הופקה או אם
 *      השליחה נכשלה."*
 *
 * ── IT OVERRIDES A RULE THIS REPOSITORY STATED, AND THE OLD RULE IS KEPT IN SIGHT ─────────────
 *
 * `PlatformTenantScreen` carried: *"NO DOWNLOAD HERE, AND THAT IS THE MASTER'S LINE. §12B.1 gives
 * the platform 'view billing & subscription history' and gives the DOWNLOAD to the tenant."* That
 * was a correct reading of §12B.1 and a wrong assumption about who settles it. The Owner has
 * settled it the other way for an administrator holding the capability 0054 created. The superseded
 * reading stays recorded — in the service, in the route and in the screen — so nobody finds the old
 * rule in one file and this component in another and has to guess which is current.
 *
 * ── TWO OF THE THREE ARE ONE REQUEST, AND THE THIRD IS A DIFFERENT AUTHORITY ──────────────────
 *
 * *"צפייה"* and *"הורדה"* both resolve one address — `Documents/CreateDocumentUrl` returns one per
 * document — and differ only in what the browser is asked to do with it. *"שליחה חוזרת"* is a POST
 * under `platform.billing.document.resend`, because reading a document and mailing it to an address
 * somebody typed are not the same act.
 *
 * ── AND IT NEVER SAYS THE MESSAGE ARRIVED ─────────────────────────────────────────────────────
 *
 * *"הצג אם ההודעה נמסרה לספק הדואר או נכשלה — לא „הגיעה"."* The server answers
 * `DeliveryResult`'s own three words and `sent` means a provider took custody. `WORDING` below is
 * the single place those three become Hebrew, and `documentResendWording.test.ts` fails if any of
 * them starts claiming delivery.
 */

import { useState } from 'react';

import { ActionFailureNotice } from './ActionFailureNotice';
import { InfoNote } from './InfoNote';
import { Modal } from './Modal';
import { callOperation } from '../api/operations';
import { classifyActionError } from '../api/actionOutcome';
import type { ActionFailure } from '../api/actionOutcome';

/**
 * THE THREE OUTCOMES, IN HEBREW, IN ONE PLACE.
 *
 * ── NOT ONE OF THEM SAYS "ARRIVED", AND THAT IS MEASURED RATHER THAN INTENDED ────────────────
 *
 * `sent` is the dangerous one: it is the success case, so it is the sentence a reader skims, and
 * *"ההודעה נשלחה"* would be read as *"the customer has it"*. `MailAccepted`'s own header is blunt
 * about the limit — a provider took custody, and knowing more needs a bounce webhook this product
 * does not have. The wording says exactly that much.
 */
const WORDING: Readonly<Record<'sent' | 'suppressed' | 'failed', { title: string; body: string }>> = {
  sent: {
    title: 'ההודעה נמסרה לספק הדואר',
    body:
      'ספק הדואר קיבל את ההודעה לשליחה. אישור שהנמען פתח או קיבל את ההודעה אינו קיים במערכת, ' +
      'ולכן אין כאן קביעה שהמסמך הגיע.',
  },
  suppressed: {
    /*
     * A SUPPRESSION IS NOT A FAILURE AND NOT A SUCCESS. On an estate with no mail provider every
     * message is correctly suppressed — the product is behaving as designed and the recipient got
     * nothing. Calling it either of the other two would send an operator to the wrong place.
     */
    title: 'ההודעה לא נשלחה — והמערכת פעלה כמתוכנן',
    body: 'הנמען לא קיבל דבר. הסיבה מופיעה למטה, והיא מצב של השרת ולא תקלה במסמך.',
  },
  failed: {
    title: 'השליחה נכשלה',
    body: 'ספק הדואר לא קיבל את ההודעה. הסיבה מופיעה למטה.',
  },
};

/**
 * THE NAMED REASONS, IN HEBREW.
 *
 * ── IT IS A LOOKUP WITH NO FALLBACK SENTENCE, ON PURPOSE ────────────────────────────────────
 *
 * A map that answered *"שליחה נכשלה"* for anything it did not recognise would make every future
 * reason invisible: a new `FailureReason` would render as the generic line and nobody would learn
 * that the estate's mail credential had been revoked. An unknown word is shown AS ITSELF beside a
 * sentence saying it is not translated, which is ugly and findable — and the Owner met three
 * untranslated English refusals on his own screen in two days, which is how we know the generic
 * sentence is the worse failure.
 */
const REASON_HE: Readonly<Record<string, string>> = {
  /* SuppressionReason */
  no_provider_configured: 'לא מוגדר שירות דואר יוצא בשרת הזה.',
  recipient_suppressed: 'הכתובת נמצאת ברשימת חסימה בעקבות החזרות קודמות.',
  recipient_unsubscribed: 'הנמען ביקש לא לקבל הודעות מהסוג הזה.',
  /* FailureReason */
  provider_rejected: 'ספק הדואר קיבל את הבקשה ודחה את ההודעה.',
  provider_unreachable: 'לא ניתן היה להגיע לספק הדואר.',
  recipient_invalid: 'ספק הדואר קבע שהכתובת אינה ניתנת לניתוב.',
  rate_limited: 'הבקשה נדחתה בגלל נפח שליחה. אפשר לנסות שוב מאוחר יותר.',
  provider_authentication_rejected:
    'ספק הדואר דחה את פרטי ההתחברות של השרת. זו תקלת הגדרה שדורשת טיפול בשרת — ניסיון חוזר לא יעזור.',
};

function reasonSentence(reason: string | null): string | null {
  if (reason === null) return null;
  const said = REASON_HE[reason];
  return said ?? `הסיבה שהשרת החזיר: ${reason}. אין לה עדיין נוסח בעברית.`;
}

type Sent = {
  readonly outcome: 'sent' | 'suppressed' | 'failed';
  readonly reason: string | null;
  readonly documentNumber: string;
  readonly sentTo: string;
};

export function DocumentActions({
  tenantUid,
  documentUid,
  documentNumber,
  billingEmail,
  canResend,
  onSent,
}: {
  readonly tenantUid: string;
  readonly documentUid: string;
  /** The provider's number. The dialog confirms it, so it is required rather than looked up. */
  readonly documentNumber: string;
  /**
   * THE OFFICE'S BILLING ADDRESS, OR NULL WHEN IT HAS NONE.
   *
   * Null is a real state and the dialog says so rather than showing an empty default: an office
   * with no billing account has no address to prefer, and the sender must type one. The SERVER
   * decides either way — it refuses `no_recipient` when neither exists — so this is what the
   * person is shown and never what is relied upon.
   */
  readonly billingEmail: string | null;
  /**
   * WHETHER THIS ADMINISTRATOR HOLDS `platform.billing.document.resend`.
   *
   * The control is WITHHELD rather than offered and refused. A button that always answers 403
   * teaches an operator the product is broken; an absent button teaches them they do not hold the
   * authority, which is the truth. The server re-decides regardless — this is a screen, not a gate.
   */
  readonly canResend: boolean;
  /** Re-read the documents, so the new send record appears beside the document it is about. */
  readonly onSent: () => void;
}): JSX.Element {
  const [opening, setOpening] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [failure, setFailure] = useState<ActionFailure | null>(null);

  /*
   * ── THE ADDRESS IS FETCHED ON THE PRESS AND NEVER ON RENDER ─────────────────────────────────
   *
   * A documents table renders many rows, and resolving every row's address on load would be one
   * request to the provider PER ROW for an address almost none of them will be asked for. It is
   * also a request that costs the provider something, which is the stronger reason.
   */
  async function open(download: boolean): Promise<void> {
    setOpening(true);
    setFailure(null);
    try {
      const { url } = await callOperation('platform.tenant.document.address', {
        params: { tenant_uid: tenantUid, document_uid: documentUid },
      });
      /*
       * ── IT HANDS THE ADDRESS TO THE BROWSER AND DOES NOT FETCH THE BYTES ───────────────────
       *
       * The document lives at the provider and this application is not a proxy for it. `view`
       * opens it in a new tab; `download` asks the browser to save it with `download` on an
       * anchor, which is a HINT — a cross-origin response may serve inline anyway, and the
       * provider's own `Content-Disposition` wins. That is why both actions share one request:
       * the difference between them is the browser's, not ours.
       *
       * `noopener` BECAUSE A NEW TAB WITH `window.opener` CAN NAVIGATE THIS ONE. The target is a
       * third party's address, and this console is a platform administration surface.
       */
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      if (download) anchor.download = `${documentNumber}.pdf`;
      anchor.click();
    } catch (err) {
      setFailure(classifyActionError(err));
    } finally {
      setOpening(false);
    }
  }

  return (
    <div data-area="document-actions">
      {failure === null ? null : <ActionFailureNotice failure={failure} />}
      <div className="actions-row">
        <button type="button" className="btn" disabled={opening} onClick={() => void open(false)}>
          צפייה
        </button>
        <button type="button" className="btn" disabled={opening} onClick={() => void open(true)}>
          הורדה
        </button>
        {!canResend ? null : (
          <button type="button" className="btn" onClick={() => setDialog(true)}>
            שליחה חוזרת במייל
          </button>
        )}
      </div>
      {!dialog ? null : (
        <ResendDialog
          tenantUid={tenantUid}
          documentUid={documentUid}
          documentNumber={documentNumber}
          billingEmail={billingEmail}
          onClose={() => setDialog(false)}
          onSent={onSent}
        />
      )}
    </div>
  );
}

function ResendDialog({
  tenantUid,
  documentUid,
  documentNumber,
  billingEmail,
  onClose,
  onSent,
}: {
  readonly tenantUid: string;
  readonly documentUid: string;
  readonly documentNumber: string;
  readonly billingEmail: string | null;
  readonly onClose: () => void;
  readonly onSent: () => void;
}): JSX.Element {
  /*
   * TWO STATES, NOT ONE EDITABLE FIELD PREFILLED WITH THE DEFAULT.
   *
   * A prefilled field cannot distinguish *"send to the billing address"* from *"send to this
   * address, which happens to equal it today"* — and 0054 stores that distinction, because the
   * billing contact CHANGES and the question *"did somebody send this to an address of their own
   * choosing"* has to stay answerable afterwards. So the choice is explicit and the body carries
   * `null` for the default rather than a copy of the address.
   */
  const [useOther, setUseOther] = useState(billingEmail === null);
  const [other, setOther] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<ActionFailure | null>(null);
  const [sent, setSent] = useState<Sent | null>(null);

  const typed = other.trim();
  /*
   * THE SAME SHAPE THE SERVER REFUSES, AND THE SERVER IS STILL THE ONE THAT DECIDES. This catches
   * the mistake while the person is still looking at the field; `address_invalid` catches it if
   * they get past this, which they can — a screen is not a validator.
   */
  const typedLooksRight = /^[^\s@]+@[^\s@]+\.[^\s@.]+$/u.test(typed);
  const destination = useOther ? typed : (billingEmail ?? '');
  const ready = useOther ? typedLooksRight : billingEmail !== null;

  async function send(): Promise<void> {
    setSending(true);
    setFailure(null);
    try {
      const result = await callOperation('platform.tenant.document.resend', {
        params: { tenant_uid: tenantUid, document_uid: documentUid },
        body: { to: useOther ? typed : null },
      });
      setSent({
        outcome: result.outcome,
        reason: result.reason,
        documentNumber: result.documentNumber,
        /* THE ADDRESS THE SERVER ACTUALLY USED, not the one this screen computed. */
        sentTo: result.sentTo,
      });
      onSent();
    } catch (err) {
      setFailure(classifyActionError(err));
    } finally {
      setSending(false);
      setConfirming(false);
    }
  }

  if (sent !== null) {
    const words = WORDING[sent.outcome];
    const why = reasonSentence(sent.reason);
    return (
      <div className="field-group" data-area="resend-result">
        <p className="notice__title">{words.title}</p>
        <p className="notice__body">
          {`מסמך ${sent.documentNumber} · `}
          <span dir="ltr">{sent.sentTo}</span>
        </p>
        <p className="notice__body">{words.body}</p>
        {why === null ? null : <p className="notice__body">{why}</p>}
        <p className="notice__body">
          השליחה נרשמה בכרטיס הלקוח עם מי שביצע אותה, המועד, הכתובת והתוצאה.
        </p>
        <div className="actions-row">
          <button type="button" className="btn" onClick={onClose}>
            סגירה
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="field-group" data-area="resend">
      {failure === null ? null : <ActionFailureNotice failure={failure} />}
      <p className="field__hint">{`שליחה חוזרת של מסמך ${documentNumber}.`}</p>

      {billingEmail === null ? (
        /*
         * NO BILLING ADDRESS IS A STATE, NOT AN EMPTY FIELD. An office with no billing account has
         * no address to prefer, and saying so sends the operator to the right place — the billing
         * account — rather than leaving them to wonder why the default is blank.
         */
        <>
          {/*
            THE STATEMENT ON SCREEN, THE CONSEQUENCE BEHIND A CONTROL. `noStandingProse.test.ts`
            caps a standing line at seventy characters, on the Owner's own instruction that a
            screen STATES and does not explain — and it fired on the paragraph this replaces.
          */}
          <p className="field__hint">לארגון הזה אין כתובת דואר לחיוב.</p>
          <p className="field__hint">יש להזין כתובת לשליחה הזאת.</p>
        </>
      ) : (
        <label className="field">
          <input
            type="radio"
            name={`resend-dest-${documentUid}`}
            checked={!useOther}
            onChange={() => setUseOther(false)}
          />
          <span className="field__label">
            {'כתובת הדואר לחיוב — '}
            <span dir="ltr">{billingEmail}</span>
          </span>
        </label>
      )}

      <label className="field">
        <input
          type="radio"
          name={`resend-dest-${documentUid}`}
          checked={useOther}
          onChange={() => setUseOther(true)}
        />
        <span className="field__label">כתובת אחרת, לשליחה הזאת בלבד</span>
      </label>
      {!useOther ? null : (
        <label className="field">
          <span className="field__label">כתובת למשלוח</span>
          <input
            className="field__input"
            name={`resend-address-${documentUid}`}
            type="email"
            dir="ltr"
            value={other}
            onChange={(e) => setOther(e.currentTarget.value)}
          />
          <span className="field__hint">לשליחה הזאת בלבד. כתובת החיוב של הארגון אינה משתנה.</span>
          {/*
            AND THE RECORD IS EXPLAINED BEHIND A CONTROL. That a typed address is MARKED as typed is
            the point of `was_default_address` being stored rather than derived — the billing
            contact changes, and *"did somebody send this to an address of their own choosing"* has
            to stay answerable afterwards. It is worth saying and it is not worth a paragraph
            standing under the field every time.
          */}
          <InfoNote summaryHe="מה נרשם על כתובה שהוזנה ידנית">
            <p>
              הכתובת אינה נשמרת כברירת מחדל ואינה משנה את כתובת החיוב של הארגון. היא נרשמת
              ברשומת השליחה ומסומנת שם ככתובת שהוזנה ידנית, כדי שיהיה אפשר לדעת זאת גם אחרי
              שכתובת החיוב תשתנה.
            </p>
          </InfoNote>
        </label>
      )}

      <div className="actions-row">
        <button
          type="button"
          className="btn btn--primary"
          disabled={!ready}
          onClick={() => setConfirming(true)}
        >
          שליחה
        </button>
        <button type="button" className="btn" onClick={onClose}>
          ביטול
        </button>
      </div>

      {!confirming ? null : (
        <Modal
          intentHe="שליחה חוזרת של מסמך"
          /*
           * 09 §2.2 — THE DOCUMENT NUMBER AND THE DESTINATION, BOTH, BEFORE THE PRESS. That is the
           * Owner's own requirement: *"תאשר את מספר החשבונית ואת היעד לפני השליחה."* Confirming one
           * of the two is what lets a correct-looking confirmation send the wrong document.
           *
           * AND IT SAYS WHAT WILL BE IN THE MESSAGE. The recipient receives a LINK to the original
           * at the document provider, not an attached file — the product deliberately keeps no copy
           * of the PDF, so there is no second original to disagree with the one the tax
           * requirements attach to. Telling the operator that is the difference between them
           * knowing what the customer will see and assuming it.
           */
          blastRadiusHe={
            `תישלח הודעה לכתובת ${destination} עם קישור למסמך המקורי ${documentNumber} אצל מפיק המסמכים. ` +
            'המסמך עצמו אינו משתנה, לא מופק מסמך חדש ולא נגבה כסף. ' +
            'ההודעה אינה נושאת קובץ מצורף: GLPOINT אינה שומרת העתק של המסמך, והקישור פותח את המקור. ' +
            'אי אפשר לבטל שליחה שיצאה.'
          }
          fields={0}
          irreversible
          confirmLabelHe={sending ? 'שולח…' : 'אישור השליחה'}
          onConfirm={() => void send()}
          onClose={() => setConfirming(false)}
        >
          <p className="form__note">
            {useOther
              ? 'הכתובת הוזנה ידנית ואינה כתובת החיוב של הארגון. היא תירשם כך ברשומת השליחה.'
              : 'הכתובת היא כתובת הדואר לחיוב של הארגון.'}
          </p>
        </Modal>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
   WHAT WAS ALREADY SENT, BESIDE THE DOCUMENT IT IS ABOUT
   ══════════════════════════════════════════════════════════════════════════════════════════════

       *"תעד מי שלח, מתי, לאיזו כתובת ומה תוצאת השליחה בכרטיס הלקוח."*

   FOUR FACTS PER ROW and the fourth is the one with a trap in it: the OUTCOME. `sent` means a
   provider took custody, so the record says *"נמסרה לספק הדואר"* and never *"הגיעה"*. It
   reads the SAME `WORDING` table the result panel does, so the two cannot drift into saying
   different things about one outcome \u2014 which is how a log and a confirmation end up disagreeing.

   IT RENDERS NOTHING WHEN NOTHING WAS SENT, and that is right here: every document in the table
   would otherwise carry an empty "no copies sent" line, which is noise on the common case. The
   READ's own failure is not silent, though \u2014 see `DocumentSendRecords` below.
   ══════════════════════════════════════════════════════════════════════════════════════════════ */

/** The read's payload, named so both helpers here and the screen agree on one shape. */
export interface SendHistory {
  readonly billingEmail: string | null;
  readonly sends: readonly {
    readonly sendUid: string;
    readonly documentUid: string;
    readonly sentTo: string;
    readonly wasDefaultAddress: boolean;
    readonly requestedAt: string;
    readonly outcome: string;
    readonly reason: string | null;
    readonly requestedByDisplayName: string | null;
  }[];
}

type Read = { readonly state: 'loading' } | { readonly state: 'loaded'; readonly data: SendHistory } | { readonly state: 'failed'; readonly failure: unknown };

/**
 * THE DEFAULT RECIPIENT, OR NULL WHILE THE READ HAS NOT ARRIVED.
 *
 * NULL FOR "LOADING" AND NULL FOR "NO ACCOUNT" IS SAFE HERE AND ONLY HERE, because the dialog
 * treats a null default as *"there is no billing address, type one"* and the SERVER refuses
 * `no_recipient` if the sender neither typed one nor had a default. The worst case of the two
 * collapsing is that an operator who opens the dialog in the first instant is asked to type an
 * address they did not have to \u2014 and the dialog's own text says why. No message can go to the
 * wrong place because of it: the body carries `null` for the default and the server resolves it.
 */
export function billingEmailOrNull(read: Read): string | null {
  return read.state === 'loaded' ? read.data.billingEmail : null;
}

export function DocumentSendRecords({
  read,
  documentUid,
}: {
  readonly read: Read;
  readonly documentUid: string;
}): JSX.Element | null {
  /*
   * A FAILED READ IS SAID, ONCE, AND NOT PER ROW. The whole tab shares one request, so a failure
   * is a fact about the tab \u2014 repeating it under every document would be the same sentence eight
   * times. It is reported where the read is owned, and here the absence is simply not claimed to
   * mean "nothing was sent": that is the distinction this product's `ServerRead` exists for.
   */
  if (read.state !== 'loaded') return null;

  const mine = read.data.sends.filter((row) => row.documentUid === documentUid);
  if (mine.length === 0) return null;

  return (
    <ul className="notice__body" data-area="send-records">
      {mine.map((row) => {
        const words = WORDING[row.outcome === 'sent' ? 'sent' : row.outcome === 'suppressed' ? 'suppressed' : 'failed'];
        const why = reasonSentence(row.reason);
        return (
          <li key={row.sendUid}>
            {`${words.title} \u00b7 `}
            <span dir="ltr">{row.sentTo}</span>
            {row.wasDefaultAddress ? '' : ' (כתובת שהוזנה ידנית)'}
            {' \u00b7 '}
            <span dir="ltr">{row.requestedAt}</span>
            {row.requestedByDisplayName === null ? '' : ` \u00b7 ${row.requestedByDisplayName}`}
            {why === null ? '' : ` \u00b7 ${why}`}
          </li>
        );
      })}
    </ul>
  );
}
